import React from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import VideoCard from "../components/VideoCard";
import VideoModal from "../components/VideoModal";
import CommissionDialog from "../components/CommissionDialog";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Sparkles, Video, Award } from "lucide-react";

const FALLBACKS = [
    "https://images.pexels.com/photos/8359960/pexels-photo-8359960.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.pexels.com/photos/8357248/pexels-photo-8357248.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.pexels.com/photos/8880280/pexels-photo-8880280.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

export default function CreatorProfile() {
    const { id } = useParams();
    const { user } = useAuth();
    const [creator, setCreator] = React.useState(null);
    const [active, setActive] = React.useState(null);
    const [commissionOpen, setCommissionOpen] = React.useState(false);

    const load = React.useCallback(() => {
        api.get(`/creators/${id}`).then((r) => setCreator(r.data));
    }, [id]);

    React.useEffect(() => {
        load();
    }, [load]);

    if (!creator)
        return (
            <div className="min-h-screen bg-white">
                <Navbar />
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-20 text-slate-500 overline">
                    Loading…
                </div>
            </div>
        );

    const canCommission = !user || user.role === "brand" || user.role === "admin";

    return (
        <div className="min-h-screen bg-white text-slate-900" data-testid="creator-profile">
            <Navbar />

            <section className="border-b hairline surface-warm">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-12 lg:py-16 grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-8 flex flex-col md:flex-row gap-6 md:items-center">
                        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-coral to-lime flex items-center justify-center text-white font-display font-bold text-4xl flex-shrink-0">
                            {creator.name?.[0]?.toUpperCase()}
                        </div>
                        <div className="flex-1">
                            <div className="chip chip-lime mb-2 inline-flex">CREATOR</div>
                            <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight" data-testid="creator-name">
                                {creator.name}
                            </h1>
                            <p className="text-slate-500 mt-2 max-w-lg">
                                {creator.bio || "Cinematic UGC and product video creator."}
                            </p>
                            <div className="flex items-center gap-3 mt-4 flex-wrap">
                                <span className="chip">
                                    <Video className="w-3 h-3" /> {creator.video_count} videos
                                </span>
                                <span className="chip chip-success">
                                    <Award className="w-3 h-3" /> Verified
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-4 flex flex-col gap-3">
                        {canCommission ? (
                            <button
                                onClick={() => {
                                    if (!user) {
                                        window.location.href = "/register";
                                        return;
                                    }
                                    setCommissionOpen(true);
                                }}
                                className="btn-pill-primary inline-flex items-center justify-center gap-2 h-12"
                                data-testid="commission-cta"
                            >
                                <Sparkles className="w-4 h-4" />
                                Request custom video
                            </button>
                        ) : null}
                        <Link to="/creators">
                            <button className="btn-pill-ghost w-full">Back to creators</button>
                        </Link>
                    </div>
                </div>
            </section>

            <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-12">
                <div className="overline text-slate-500 mb-4">PORTFOLIO</div>
                {creator.videos.length === 0 ? (
                    <div className="card-ecom p-12 text-center" data-testid="empty-portfolio">
                        <p className="text-slate-500">This creator hasn&apos;t uploaded yet.</p>
                    </div>
                ) : (
                    <div
                        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5"
                        data-testid="portfolio-grid"
                    >
                        {creator.videos.map((v, idx) => (
                            <VideoCard
                                key={v.id}
                                video={v}
                                onClick={setActive}
                                fallbackThumb={FALLBACKS[idx % FALLBACKS.length]}
                            />
                        ))}
                    </div>
                )}
            </section>

            <VideoModal
                video={active}
                onClose={() => setActive(null)}
                onCommissionRequest={() => setCommissionOpen(true)}
            />
            <CommissionDialog
                open={commissionOpen}
                creator={creator}
                onClose={() => setCommissionOpen(false)}
            />
        </div>
    );
}
